import { useState } from "react";
import { cn } from "../../lib/utils";
import type { ContentList } from "../../lib/types";

interface CTAProps {
  title?: string;
  description?: string;
  buttonText?: string;
  href?: string;
  variant?: "primary" | "secondary";
  className?: string;
  contentList?: ContentList;
}

export function CTA({
  title = "Ready to transform your back office?",
  description = "Get in touch to see how CoPlane can automate your operations.",
  buttonText = "Get Started",
  variant = "primary",
  contentList = "website_signups",
  className,
}: CTAProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch("/api/sign-up", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email,
          ...(contentList && { content_list: contentList })
        }),
      });

      const data = await response.json() as { error?: string; success?: boolean };

      if (!response.ok) {
        throw new Error(data.error || "Something went wrong");
      }

      setStatus("success");
      setEmail("");
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(error.message);
    }
  };

  return (
    <div className={cn("my-16 overflow-hidden rounded-2xl border border-black/5 bg-gray-50 p-8 text-center dark:bg-white/5 md:p-12", className)}>
      <h3 className="mb-4 font-display text-2xl font-semibold text-black dark:text-white md:text-3xl">
        {title}
      </h3>
      <p className="mb-8 text-lg text-black/70 dark:text-white/70">
        {description}
      </p>

      {status === "success" ? (
        <div className="rounded-lg bg-[rgb(100.7,185.1,241.1)]/20 p-4 text-[rgb(62,132,229)] dark:bg-[rgb(100.7,185.1,241.1)]/20 dark:text-[rgb(184.7,207.8,219.4)]">
          <p className="font-medium">Thanks for reaching out!</p>
          <p className="text-sm">We'll be in touch shortly.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mx-auto max-w-md flex flex-col gap-4 sm:flex-row">
          <input
            type="email"
            placeholder="Enter your work email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1 rounded-full border border-black/10 bg-white px-6 py-3.5 text-base text-black shadow-sm placeholder:text-black/50 transition-all focus:border-black focus:outline-none focus:ring-2 focus:ring-black/10 dark:border-white/20 dark:bg-white/10 dark:text-white dark:placeholder:text-white/50 dark:focus:border-white dark:focus:ring-white/10"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className={cn(
              "inline-flex h-[50px] cursor-pointer items-center justify-center whitespace-nowrap rounded-full px-8 text-base font-semibold shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-50",
              variant === "primary"
                ? "bg-black text-white hover:bg-black/90 hover:shadow-md dark:bg-white dark:text-black dark:hover:bg-white/95"
                : "bg-white text-black border border-black/20 hover:bg-gray-50 hover:shadow-md dark:bg-white/10 dark:text-white dark:border-white/20 dark:hover:bg-white/20"
            )}
          >
            {status === "loading" ? "Sending..." : buttonText}
          </button>
        </form>
      )}
      
      {status === "error" && (
        <p className="mt-4 text-sm text-red-600 dark:text-red-400">
          {errorMessage}
        </p>
      )}
      
      <p className="mt-4 text-xs text-black/40 dark:text-white/40">
        By submitting, you agree to our{' '}
        <a href="/legal/privacy-policy" className="underline hover:text-black/60 dark:hover:text-white/60">
          privacy policy
        </a>.
      </p>
    </div>
  );
}

